import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AppState } from 'common/state-type'
import Constants from 'common/utils/constants'
import throttle from 'lodash/throttle'
import usePodcasts from 'common/providers/usePodcasts'
import Project from 'common/project'
import _data from 'common/utils/_data'
import { useNavigate } from 'react-router-dom'

const SEARCH_DELAY = 500

export const SORT_OPTIONS: {
  label: string
  value: string
  sort: 'title' | 'published_at'
  order: 'ASC' | 'DESC'
}[] = [
  {
    label: 'Name (A-Z)',
    value: 'name-az',
    sort: 'title',
    order: 'ASC',
  },
  {
    label: 'Name (Z-A)',
    value: 'name-za',
    sort: 'title',
    order: 'DESC',
  },
  {
    label: 'Newest to Oldest',
    value: 'newest',
    sort: 'published_at',
    order: 'DESC',
  },
  {
    label: 'Oldest to Newest',
    value: 'oldest',
    sort: 'published_at',
    order: 'ASC',
  },
]

export const usePodcastsTemplate = ({
  pageSize = 20,
}: {
  pageSize?: number
  id?: string
} = {}) => {
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[0])
  const [podcastFilter, setPodcastFilter] = useState<string | undefined>(
    undefined,
  )
  const isLoadingMore = useRef(false)
  const [search, setSearch] = useState('')
  const [searchValue, setSearchValue] = useState('')

  const [page, setPage] = useState(1)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined,
  )

  const [topSpeakers, setTopSpeakers] = useState<any>([])
  const [featuredHome, setFeaturedHome] = useState<any>([])
  const [podcastMetrics, setPodcastMetrics] = useState<{
    podcastCount: number
    guestCount: number
    areasCount: number
  }>({ podcastCount: 0, guestCount: 0, areasCount: 0 })
  const { podcasts, getPodcasts, podcastsLoading } = usePodcasts()
  const [previousPodcast, setPreviousPodcast] = useState<
    AppState['podcasts']['results']
  >([])

  const [episode, setEpisode] = useState<AppState['articles']['results']>([])

  const throttledSetTitle = useCallback(
    throttle(setSearch, SEARCH_DELAY, { leading: true }),
    [],
  )

  const updateSearchValue = useCallback(
    (text: string) => {
      setSearchValue(text)
      throttledSetTitle(text)
    },
    [throttledSetTitle],
  )

  const loadEpisode = useCallback(
    (id) => () => {
      fetch(`${Project.liveApi}website/podcasts/${id}`)
        .then((response) => response.json())
        .then((data) => setEpisode(data))
    },
    [],
  )

  const getPodcastMetrics = useCallback(() => {
    fetch(`${Project.liveApi}website/podcasts/metrics`)
      .then((response) => response.json())
      .then((data) =>
        setPodcastMetrics({
          podcastCount: data.podcast_count,
          guestCount: data.guest_count,
          areasCount: data.areas_count,
        }),
      )
  }, [])

  const loadSpeakers = useCallback(() => {
    fetch(`${Project.liveApi}website/interviews/top-speakers/`)
      .then((response) => response.json())
      .then((data) => setTopSpeakers(data))
  }, [])

  const loadPodcasts = useCallback(() => {
    getPodcasts(
      {
        is_template: Constants.isAvvokaEnabled,
        search,
        sort: sortOption.sort,
        order: sortOption.order,
        type: selectedType,
        document_filters: podcastFilter,
        page,
        page_size: pageSize,
      },
      {
        onSuccess: (data) => {
          setIsRefreshing(false)
          isLoadingMore.current = false
        },
        onError: () => {
          setIsRefreshing(false)
          isLoadingMore.current = false
        },
      },
    )
  }, [
    sortOption,
    search,
    page,
    selectedType,
    podcastFilter,
    pageSize,
    getPodcasts,
  ])

  const loadFeaturedHomePodcasts = useCallback(() => {
    fetch(`${Project.liveApi}website/discover-law/featured-podcasts/`)
      .then((response) => response.json())
      .then((data) => setFeaturedHome(data))
  }, [])

  const loadMore = useCallback(() => {
    if (!podcasts?.next || isLoadingMore.current) {
      return
    }

    isLoadingMore.current = true

    setPreviousPodcast([...previousPodcast, ...(podcasts?.results ?? [])])
    setPage(page + 1)
  }, [podcasts, page])

  const refresh = useCallback(() => {
    setIsRefreshing(true)
    setPreviousPodcast([])
    loadPodcasts()
  }, [loadPodcasts])

  const results = useMemo(() => {
    const docs = [...previousPodcast, ...(podcasts?.results ?? [])]

    // Check for uniqueness based on ID, just in case.
    return docs.filter(
      (value, index, self) =>
        index === self.findIndex(({ id }) => id === value.id),
    )
  }, [previousPodcast, podcasts])

  return {
    results,
    topSpeakers,
    podcastMetrics,
    getPodcastMetrics,
    isLoading: podcastsLoading,
    loadMore,
    selectedType,
    setSelectedType,
    sortOption,
    setSortOption,
    podcastFilter,
    setPodcastFilter,
    searchValue,
    updateSearchValue,
    hasNext: !!podcasts?.next,
    refresh,
    isRefreshing,
    loadEpisode,
    episode,
    loadPodcasts,
    loadSpeakers,
    featuredHome,
    loadFeaturedHomePodcasts,
  }
}
